import { useState } from 'react';
import { useOwnerStore } from '@/lib/owner-store';
import { Mail, Building2, Pencil, Trash2 } from 'lucide-react';
import type { TaskOwner } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { OwnerForm } from './owner-form';
import toast from 'react-hot-toast';

export function OwnerTable() {
  const { owners, updateOwner, deleteOwner } = useOwnerStore();
  const [editingOwner, setEditingOwner] = useState<TaskOwner | null>(null);

  const handleEdit = async (data: { name: string; email?: string; organization?: string }) => {
    if (!editingOwner) return;
    try {
      await updateOwner(editingOwner.id, data);
      toast.success('Owner updated successfully');
      setEditingOwner(null);
    } catch (error) {
      toast.error('Failed to update owner');
    }
  };

  const handleDelete = async (owner: TaskOwner) => {
    if (!confirm(`Are you sure you want to delete ${owner.name}?`)) return;
    try {
      await deleteOwner(owner.id);
      toast.success('Owner deleted successfully');
    } catch (error) {
      toast.error('Failed to delete owner');
    }
  };

  if (owners.length === 0) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 p-4">
        No owners found
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Organization</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {owners.map((owner) => (
              <tr 
                key={owner.id}
                className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <td className="px-4 py-2 font-medium">{owner.name}</td>
                <td className="px-4 py-2">
                  {owner.email && (
                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                      <Mail className="w-4 h-4" />
                      {owner.email}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2">
                  {owner.organization && (
                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                      <Building2 className="w-4 h-4" />
                      {owner.organization}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingOwner(owner)}
                      className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(owner)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editingOwner} onOpenChange={(open) => !open && setEditingOwner(null)}>
        <DialogContent>
          <DialogTitle>Edit Owner</DialogTitle>
          {editingOwner && (
            <OwnerForm
              defaultValues={{
                name: editingOwner.name,
                email: editingOwner.email || '',
                organization: editingOwner.organization || ''
              }}
              onSubmit={handleEdit}
              onCancel={() => setEditingOwner(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}